import React, { useState, useEffect, createContext, useContext } from 'react';
import { toast } from 'react-toastify';
import ApiServices from '../../shared/ApiServices/ApiServices';
import axios from 'axios';

// Create the context
const fileUploadsConfigContext = createContext(null);
const { Provider } = fileUploadsConfigContext;

// Custom hook to use the file upload context
export const useFileUpload = () => useContext(fileUploadsConfigContext);

// dicomMimeTypes
const dicomMimeTypes = [
    'application/dicom',
    'application/dicom+json',
    'application/dicom+xml',
    'application/octet-stream',
    'application/dicom-rle',
    'image/dicom-rle',
    'image/dicom',
    'application/dicom+zip',
    'application/x-dicom',
    'application/vnd.dicom',
    'image/x-dicom',
    'application/x-dcm',
    'application/x-dicom+xml',
    'application/dicom+pdf',
    'application/dicom+dicomdir',
];

// Provider component
export function FileUploadProvider({ children }) {
    const [selectedImages, setSelectedImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadId, setUploadId] = useState(null);
    const [showModal, setShowModal] = useState(false);


    //** Simple Drag and Drop States Starts  **/
    const [isDraggingOver, setIsDraggingOver] = useState(false);

    const showFinalToast = (messageType: string) => {
        let message = '';
        let toastType = ""

        switch (messageType) {
            case 'upload-success':
                message = 'Files uploaded successfully!';
                toastType = 'success';
                break;

            case 'no-files':
                message = 'No files selected for upload.';
                toastType = 'error';
                break;

            case 'unsupported-file':
                message = 'Unsupported file type. Please upload a valid image file.';
                toastType = 'error';
                break;

            case 'file-limit-exceeded':
                message = 'You can upload a maximum of 100 files at a time.';
                toastType = 'error';
                break;

            case 'upload-failed':
                message = 'Upload failed. Please try again.';
                toastType = 'error';
                break;
        }

        toast[toastType](message);
    }

    const resetFiles = () => {
        setUploadProgress(0);
        setSelectedImages([]);
        setUploadId(null);
    };

    const generateUniqueFileName = (originalName: string) => {
        const extension = originalName.split('.').pop();
        const dateHex = Date.now().toString(16);
        const randomHex = Math.floor(Math.random() * 0xffffff).toString(16);
        return `${dateHex}-${randomHex}.${extension}`;
    };


    const handleFileChange = (files: File[], quickType: boolean = false) => {
        const newSelectedImages = [...selectedImages, ...files].map((file) => {
            const uniqueName = generateUniqueFileName(file.name);
            const renamedFile = new File([file], uniqueName, { type: file.type });
            return renamedFile;
        });

        if (newSelectedImages.length > 100) {
            showFinalToast('file-limit-exceeded');
        } else {
            setSelectedImages(newSelectedImages);
        }
        console.log('Selected files:', newSelectedImages);
        if (quickType) handleQuickUpload(newSelectedImages)
    };

    const appendFormData = (objectFields, file) => {
        const formData = new FormData();
        for (const key in objectFields) {
            formData.append(key, objectFields[key]);
        }
        formData.append('file', file);
        return formData;
    };

    const getFileType = (file) => {
        const fileType = file.type;
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.split('.').pop();

        if (fileType === 'application/zip' || fileExtension === 'zip') {
            return 'zip';
        }
        else if (dicomMimeTypes.includes(fileType) || fileExtension === 'dcm') {
            return 'dcm';
        }

        return 'unknown';
    };

    const handleModalFileUpload = async (selectedPatientId: string) => {
        await promiseUpload(selectedImages, selectedPatientId);
    };

    const handleQuickUpload = async (quickImages: File[]) => {
        toast.promise(promiseUpload(quickImages, "dummy-patient", true), {
            pending: 'Uploading Files..',
            success: 'Uploaded Files Successfully',
            error: 'Upload failed. Please try again.',
        });
        resetFiles();
    }

    const promiseUpload = async (allSelectedFiles: File[], patientId: string, quickType: boolean = false) => {
        if (allSelectedFiles.length === 0) {
            showFinalToast('no-files');
            throw new Error('no-files');
        }

        console.log('Files to be uploaded:', allSelectedFiles);

        try {
            setUploading(true);
            // Step 1: Create upload ID

            const uploadRes = await ApiServices.createUploadId("PATIENT#" + patientId);
            const newUploadId = uploadRes.uploadId;
            setUploadId(newUploadId);

            // Step 2: Get pre-signed URLs

            const fileType = getFileType(allSelectedFiles[0]);
            if (fileType === 'unknown') {
                showFinalToast('unsupported-file');
                setUploading(false);
                throw new Error('unsupported-file');
            }

            const preAssignedData = await ApiServices.getGroupPreAssignedUrl(newUploadId, allSelectedFiles.length, fileType);

            // Step 3: Upload files using pre-signed URLs

            const totalFiles = allSelectedFiles.length;
            const progressArray = new Array(totalFiles).fill(0);

            const uploadPromises = preAssignedData.map((item, index) => {
                const formData = appendFormData(item.fields, allSelectedFiles[index]);
                return axios.post(item.url, formData, {
                    onUploadProgress: progressEvent => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        progressArray[index] = percentCompleted;
                        const totalProgress = progressArray.reduce((acc, curr) => acc + curr, 0) / totalFiles;
                        setUploadProgress(Math.round(totalProgress));
                    },
                });
            });

            await Promise.all(uploadPromises);
            // Step 4: Notify backend of completed upload

            await ApiServices.uploadCompleted(newUploadId);
            setUploading(false);
            if (!quickType) showFinalToast('upload-success');

        } catch (error) {
            setUploading(false);
            if (!quickType) showFinalToast('upload-failed');
        }
    }

    //** Simple Drag and Drop Functionality Starts  **/
    const LayoutDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        if (checkModalOpen()) return
        event.preventDefault();
        if (isDraggingOver) return;
        setIsDraggingOver(true);
    };

    const LayoutDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        if (checkModalOpen()) return
        event.preventDefault();
    };

    const LayoutDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        if (checkModalOpen()) return
        event.preventDefault()
        if (!isDraggingOver) return;
        setIsDraggingOver(false);
    };


    const checkModalOpen = () => {
        if (showModal) {
            if (isDraggingOver) setIsDraggingOver(false)
            return true
        }
        else return false
    }

    // state configuration
    const fileUploadConfig = {
        showFinalToast,
        generateUniqueFileName,
        selectedImages,
        setSelectedImages,
        uploading,
        setUploading,
        resetFiles,
        handleFileChange,
        uploadProgress,
        handleModalFileUpload,
        LayoutDragEnter,
        LayoutDragLeave,
        LayoutDragOver,
        isDraggingOver,
        setIsDraggingOver,
        showModal,
        setShowModal
    }

    return <Provider value={fileUploadConfig}>{children}</Provider>;
}



export default FileUploadProvider;
